import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useMemo } from 'react';
import cn from 'classnames';
import { debounce } from 'lodash-es';
import { OnboardingWidgetState } from 'anchor-scout-common/types';
import { Button, Callout, Page, useOverlayContext, useStateInRef, useDevice } from 'seagull';
import { getTestIds } from 'seagull/utils/TestIds';
import { AnalystAgentPane } from '~/components/ai/AnalystAgentPane/AnalystAgentPane';
import { makeSentryIgnoredError } from '~/components/ErrorBoundaryPage';
import { SalesTaxDashboardCallout } from '~/components/SalesTaxDashboardCallout/SalesTaxDashboardCallout';
import { useIoContext, useStore, useStoreActions } from '~/contexts';
import { usePersonaKYBStatus } from '~/hooks/reactQuery/queries/usePersonaKYBStatus';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { useGetInfo } from '~/hooks/useGetInfo';
import { useOnMount } from '~/hooks/useOnMount';
import { useSetDocumentTitle } from '~/hooks/useSetDocumentTitle';
import { useStartKYC } from '~/hooks/useStartKYCPersona';
import { useLocaleKeys } from '~/i18n';
import InvoicesDigestWidget from '~/pages/VendorDashboard/InvoicesDigestWidget/InvoicesDigestWidget';
import { selectors } from '~/store/state-selectors';
import { CreateProposalButton } from '../../components/CreateProposalButton/CreateProposalButton';
import BusinessSetupWidget from './BusinessSetupWidget/BusinessSetupWidget';
import HistoricAgreementStatsWidget from './HistoricAgreementStatsWidget/HistoricAgreementStatsWidget';
import { useUpdateOnboardingState } from './hooks/useUpdateOnboardingState';
import PayoutsSummaryWidget from './PayoutsSummaryWidget/PayoutsSummaryWidget';
import RevenueWidget from './RevenueWidget/RevenueWidget';
import SalesWidget from './SalesWidget/SalesWidget';
import styles from './VendorDashboard.module.scss';
import { getExternalAccountWarning, getKycWarning, kycWarningTextsPath } from './VendorDashboard.utils';
export const GetVendorDashboardTID = (parentTestId)=>getTestIds({
        parentTestId,
        deprecatedTestIds: {
            VendorDashboardTID: {
                Root: 'vendor-dashboard',
                Widgets: 'vendor-dashboard-widgets',
                NoBusiness: 'vendor-dashboard-no-business'
            }
        }
    });
export function VendorDashboard() {
    const { isMobileDevice } = useDevice();
    const { VendorDashboardTID } = useMemo(()=>GetVendorDashboardTID(), []);
    const { ANALYST_AGENT } = useFeatureFlags();
    const showAnalyst = ANALYST_AGENT && !isMobileDevice;
    const { t: dashboardTexts } = useLocaleKeys('pages.dashboard');
    const { t: kycWarningTexts } = useLocaleKeys(kycWarningTextsPath);
    const hasBusiness = useStore(selectors.user.hasBusiness);
    const userFirstName = useStore(selectors.user.firstName) || '';
    const isMemberUser = useStore(selectors.user.isMemberUser);
    const { startKYC } = useStartKYC();
    const { logBI } = useStoreActions();
    const { businessId, businessInfo, optimisticUpdate } = useGetInfo();
    const { updateOnboardingState } = useUpdateOnboardingState();
    const getStateInRef = useStateInRef({
        businessId,
        businessInfo,
        updateOnboardingState,
        optimisticUpdate
    });
    const { getNow } = useIoContext();
    const { getOpenedModals } = useOverlayContext();
    const coveredByModal = getOpenedModals().length > 0;
    const worksAt = useStore(selectors.user.business);
    const isVendor = useStore(selectors.user.isVendor);
    const { personaKYBStatus, isLoading: isKybLoading, error: kybError } = usePersonaKYBStatus({
        refreshOnHookMount: true,
        enabled: isVendor
    });
    const kycWarning = getKycWarning(worksAt, personaKYBStatus != null && personaKYBStatus.timestamp > getNow().getTime() - 1000 * 60 * 5, kycWarningTexts);
    const externalAccountWarning = worksAt ? getExternalAccountWarning(worksAt) : null;
    useSetDocumentTitle(dashboardTexts.pageTitle());
    const debouncedUpdateOnboardingState = useMemo(()=>debounce((expanded)=>{
            const { businessId, businessInfo, updateOnboardingState, optimisticUpdate } = getStateInRef();
            if (businessInfo) {
                const onboardingWidgetState = expanded ? OnboardingWidgetState.StaticOpened : OnboardingWidgetState.Hidden;
                updateOnboardingState({
                    businessId: businessId,
                    onboardingWidgetState
                });
                optimisticUpdate((old)=>_object_spread_props(_object_spread({}, old), {
                        onboardingWidgetState
                    }));
            }
        }, 10000), []);
    useOnMount(()=>{
        return ()=>{
            debouncedUpdateOnboardingState.flush();
        };
    });
    const setupWidgetViewingState = useMemo(()=>{
        if (coveredByModal && (businessInfo === null || businessInfo === void 0 ? void 0 : businessInfo.onboardingWidgetState) === OnboardingWidgetState.ShouldReveal) {
            return null;
        }
        return businessInfo === null || businessInfo === void 0 ? void 0 : businessInfo.onboardingWidgetState;
    }, [
        businessInfo === null || businessInfo === void 0 ? void 0 : businessInfo.onboardingWidgetState,
        coveredByModal
    ]);
    if (hasBusiness && kybError) throw makeSentryIgnoredError('Error fetching persona KYB status');
    const renderOnboardingWidget = ()=>setupWidgetViewingState ? /*#__PURE__*/ _jsx(BusinessSetupWidget, {
            viewingState: setupWidgetViewingState,
            onAnimationEnd: debouncedUpdateOnboardingState
        }) : null;
    const renderDashboard = ()=>/*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                kycWarning && !isKybLoading && /*#__PURE__*/ _jsx(Callout, {
                    intent: "warning",
                    hideIcon: true,
                    className: styles.kycIncompleteBanner,
                    testId: kycWarning.testId,
                    withAction: /*#__PURE__*/ _jsx(Button, {
                        bi: {
                            name: 'kyc_warning_start_kyc'
                        },
                        onClick: ()=>{
                            logBI(11203);
                            startKYC();
                        },
                        skin: "monochrome",
                        children: dashboardTexts.kycWarning.buttonText()
                    }),
                    children: kycWarning.text
                }),
                externalAccountWarning && /*#__PURE__*/ _jsx(Callout, {
                    intent: "warning",
                    testId: externalAccountWarning.testId,
                    children: externalAccountWarning.text
                }),
                /*#__PURE__*/ _jsx(SalesTaxDashboardCallout, {}),
                !isMemberUser ? renderOnboardingWidget() : null,
                /*#__PURE__*/ _jsxs("div", {
                    className: cn(styles.widgets, {
                        [styles.mobile]: isMobileDevice
                    }),
                    "data-testid": VendorDashboardTID.Widgets(),
                    children: [
                        /*#__PURE__*/ _jsx(InvoicesDigestWidget, {}),
                        /*#__PURE__*/ _jsx(HistoricAgreementStatsWidget, {}),
                        /*#__PURE__*/ _jsx(PayoutsSummaryWidget, {}),
                        /*#__PURE__*/ _jsx(RevenueWidget, {}),
                        /*#__PURE__*/ _jsx(SalesWidget, {})
                    ]
                })
            ]
        });
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            showAnalyst && /*#__PURE__*/ _jsx(AnalystAgentPane, {}),
            /*#__PURE__*/ _jsx(Page, {
                testId: VendorDashboardTID.Root(),
                header: dashboardTexts.greeting({
                    firstName: userFirstName
                }),
                actions: /*#__PURE__*/ _jsx(CreateProposalButton, {}),
                minWidth: "1090px",
                children: hasBusiness ? renderDashboard() : /*#__PURE__*/ _jsx("div", {
                    "data-testid": VendorDashboardTID.NoBusiness()
                })
            })
        ]
    });
}
